/* JB20 first-purchase offer: regular price and native cart fallback. */
(() => {
  'use strict';

  if (window.FinectBuyboxV2) {
    window.FinectBuyboxV2.init(document);
    return;
  }

  const instances = new Map();
  const mobile = window.matchMedia('(max-width: 749px)');

  function ensureExistingShareComponent(root) {
    if (window.customElements && window.customElements.get('share-button')) return;
    const source = root.dataset.finectShareScript;
    if (!source) return;
    const path = new URL(source, document.baseURI).pathname;
    const alreadyRequested = Array.from(document.scripts).some((script) =>
      script.src && new URL(script.src, document.baseURI).pathname === path
    );
    if (alreadyRequested) return;
    // Wokiee's original share.js defines DetailsDisclosure without a guard.
    // Load its existing asset once, only if no other section has requested it.
    const script = document.createElement('script');
    script.src = source;
    script.defer = true;
    script.dataset.finectShareScript = 'true';
    document.head.appendChild(script);
  }

  function initOne(root) {
    if (instances.has(root)) return;
    const form = root.querySelector('[data-finect-form]');
    const quantity = root.querySelector('[data-finect-quantity]');
    const variantInput = root.querySelector('[data-finect-variant-id]');
    const mainButton = root.querySelector('[data-finect-main-submit]');
    const sticky = document.getElementById(root.dataset.stickyId);
    const stickyButton = sticky && sticky.querySelector('[data-finect-sticky-submit]');
    if (!form || !quantity || !variantInput || !mainButton || !sticky || !stickyButton) return;

    let variants;
    try {
      variants = JSON.parse(root.querySelector('[data-finect-variants]').textContent);
    } catch (error) {
      // The server-rendered native form remains usable if enhancement data is absent.
      return;
    }
    const variantMap = new Map(variants.map((variant) => [String(variant.id), variant]));
    const decrement = root.querySelector('[data-finect-quantity-step="-1"]');
    const quantityButtons = Array.from(root.querySelectorAll('[data-finect-quantity-step]'));
    const removeListeners = [];
    let frame = 0;
    let observer = null;

    function listen(target, name, handler, options) {
      target.addEventListener(name, handler, options);
      removeListeners.push(() => target.removeEventListener(name, handler, options));
    }

    function normalizedQuantity() {
      const value = Number(quantity.value);
      return Number.isFinite(value) ? Math.max(1, Math.min(Number.MAX_SAFE_INTEGER, Math.floor(value))) : 1;
    }

    function updateQuantity(normalize) {
      if (normalize) quantity.value = String(normalizedQuantity());
      if (decrement) decrement.disabled = normalizedQuantity() <= 1;
    }

    function applyVariant(id) {
      const variant = variantMap.get(String(id));
      const available = Boolean(variant && variant.available);
      mainButton.disabled = !available;
      stickyButton.disabled = !available;
      root.querySelectorAll('[data-finect-submit-label]').forEach((label) => {
        label.textContent = available ? 'Add to Cart' : 'Sold out';
      });
      sticky.querySelector('[data-finect-submit-label]').textContent = available ? 'Add to Cart' : 'Sold out';
      const stock = root.querySelector('[data-finect-stock]');
      stock.classList.toggle('is-sold-out', !available);
      stock.querySelector('i').hidden = !available;
      stock.querySelector('[data-finect-stock-label]').textContent = available ? 'In stock' : 'Sold out';
      if (variant) {
        variantInput.value = String(variant.id);
        renderOffer();
      }
      return available;
    }

    function setSticky(visible) {
      const show = Boolean(visible && mobile.matches && root.isConnected);
      // Both the hidden bar and its button stay out of the keyboard focus order.
      sticky.hidden = !show;
      sticky.inert = !show;
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.classList.toggle('is-on', show);
      stickyButton.tabIndex = show ? 0 : -1;
    }

    function updateSticky() {
      frame = 0;
      setSticky(mainButton.getBoundingClientRect().bottom <= 0);
    }

    function scheduleSticky() {
      if (!frame) frame = window.requestAnimationFrame(updateSticky);
    }

    quantityButtons.forEach((button) => listen(button, 'click', () => {
      quantity.value = String(Math.max(1, normalizedQuantity() + Number(button.dataset.finectQuantityStep)));
      updateQuantity(true);
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    listen(quantity, 'input', () => updateQuantity(false));
    listen(quantity, 'change', () => updateQuantity(true));
    listen(variantInput, 'change', () => applyVariant(variantInput.value));
    // Optional scoped integration hook; no assumptions about Wokiee's variant event names.
    listen(root, 'finect:variant-change', (event) => {
      const variant = event.detail && event.detail.variant;
      const id = variant ? variant.id : event.detail && event.detail.id;
      if (id != null) applyVariant(id);
    });
    const firstPercent = Math.max(0, Math.min(100, Number(root.dataset.firstPercent) || 0));
    let submitting = false;
    const money = (cents) => new Intl.NumberFormat('en-US', {style:'currency', currency:root.dataset.currency || 'USD'}).format(cents / 100);
    function renderOffer() {
      const variant = variantMap.get(String(variantInput.value));
      if (!variant) return;
      root.querySelector('[data-finect-price]').textContent = money(variant.cents);
      sticky.querySelector('[data-finect-sticky-price]').textContent = money(variant.cents);
      const firstPrice = root.querySelector('[data-first-price]');
      if (firstPrice) firstPrice.textContent = money(Math.round(variant.cents * (1-firstPercent/100)));
    }
    listen(form, 'submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (submitting) return;
      updateQuantity(true);
      if (!applyVariant(variantInput.value)) return;
      submitting = true;
      mainButton.disabled = true;
      stickyButton.disabled = true;
      // Keep the original native /cart/add and return_to=/checkout path.
      HTMLFormElement.prototype.submit.call(form);
    }, true);
    listen(window, 'pageshow', () => {
      submitting = false;
      applyVariant(variantInput.value);
    });
    const getCode = root.querySelector('[data-finect-get-code]');
    if (getCode) listen(getCode, 'click', () => {
      const dialog = document.getElementById('finect-welcome');
      const backdrop = document.getElementById('finect-welcome-backdrop');
      if (!dialog || typeof dialog.show !== 'function') {
        const status = root.querySelector('[data-finect-code-status]');
        if (status) {
          status.textContent = 'The signup form is unavailable. Please try again later.';
          status.hidden = false;
        }
        return;
      }
      if (backdrop) backdrop.hidden = false;
      if (!dialog.open) dialog.show();
      const email = dialog.querySelector('input[type="email"]');
      if (email) email.focus();
      // Existing popup owns subscription and dismissal. No cart or checkout action.
    });

    // A transformed/sticky Wokiee column must not become the fixed bar's viewport.
    // form="..." still submits exactly the same quantity + variant + return_to.
    document.body.appendChild(sticky);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        setSticky(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
      }, { threshold: 0 });
      observer.observe(mainButton);
    } else {
      listen(window, 'scroll', scheduleSticky, { passive: true });
    }
    listen(window, 'resize', scheduleSticky, { passive: true });
    listen(window, 'pageshow', scheduleSticky);
    if (mobile.addEventListener) listen(mobile, 'change', scheduleSticky);
    else {
      mobile.addListener(scheduleSticky);
      removeListeners.push(() => mobile.removeListener(scheduleSticky));
    }

    instances.set(root, () => {
      if (observer) observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      removeListeners.forEach((remove) => remove());
      sticky.remove();
      instances.delete(root);
    });
    updateQuantity(true);
    applyVariant(variantInput.value);
    updateSticky();
    ensureExistingShareComponent(root);
  }

  function init(scope) {
    if (scope.matches && scope.matches('[data-finect-buybox]')) initOne(scope);
    scope.querySelectorAll('[data-finect-buybox]').forEach(initOne);
  }

  window.FinectBuyboxV2 = { init };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((destroy, root) => {
      if (event.target === root || event.target.contains(root)) destroy();
    });
  });
})();
